import { useSelector } from "react-redux";
import { API_BASE_URL } from "../../props";
import { flatten, pick, pipe, map, values } from "ramda";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  useGet,
  usePost,
  useUpload,
  useUpdate,
  useDelete,
  usePut,
  doDelete,
} from "../../common/fetchServices";
import { useCurrentOrgId } from "../../auth/authUtils";
import { getQueryParams } from "../../util/fetchUtils";
import { notBlank } from "../../util/ext";

const getCollectionParents = pipe(
  pick(["parentModules", "anthology"]),
  values,
  flatten,
);

const getCreateCollectionUrl = () => `${API_BASE_URL}/collections`;
const getCollectionUrl = id => `${API_BASE_URL}/collections/${id}`;
const getPatchCollectionUrl = ({ id }) => `${API_BASE_URL}/collections/${id}`;
const getCollectionMediaUrl = ({ id }) =>
  `${API_BASE_URL}/collections/${id}/media`;
const getCollectionMediaIdsUrl = ({ id }) =>
  `${API_BASE_URL}/collections/${id}/media/ids`;
const getCollectionContentUrl = id =>
  `${API_BASE_URL}/collections/${id}/content`;
const getDeleteParentUrl = ({ collectionId, parentId }) =>
  `${API_BASE_URL}/collections/${collectionId}/parent/${parentId}`;
const getCollectionContentTypeSummaryUrl = id =>
  `${API_BASE_URL}/collections/${id}/content/content-type-summary`;
const getThumbnailUploadUrl = ({ id }) =>
  `${API_BASE_URL}/admin/collection/${id}/thumbnail`;

export const useCollection = id => {
  const orgId = useCurrentOrgId();
  const url = getCollectionUrl(id);
  return useGet(["collection", { id, orgId }], url);
};

export const useCollectionUpdate = () => {
  return useUpdate(getPatchCollectionUrl, {
    queriesToInvalidate: [
      ["admin-media"],
      ["anthologyCollections"],
      ["anthology-collections-creator"],
      ["collection", ["id"]],
      ["moduleMedia"],
      ["search"],
      ["unorganized-content-groups"],
    ],
  });
};

export const useRemoveContentFromCollection = () => {
  const auth = useSelector(state => state.auth);
  const queryClient = useQueryClient();
  const currentOrgId = useCurrentOrgId();

  return useMutation({
    mutationFn: async ({
      collectionId,
      contentIds,
      stopInvalidation = false,
    }) => {
      const url = getCollectionMediaIdsUrl({ id: collectionId });
      return doDelete(auth)(url, {
        data: { ids: contentIds },
      });
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["search"] });
      const { collectionId, stopInvalidation = false } = variables;
      queryClient.invalidateQueries({
        queryKey: ["collection", { id: collectionId }],
      });
      queryClient.invalidateQueries({
        queryKey: ["collection-content", { id: collectionId }],
      });
      queryClient.invalidateQueries({
        queryKey: [
          "collection-content-type-summary",
          currentOrgId,
          { id: collectionId },
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [
          "collection-approved-content",
          currentOrgId,
          { id: collectionId },
        ],
      });
      queryClient.invalidateQueries({ queryKey: ["moduleApprovedContent"] });
      queryClient.invalidateQueries({ queryKey: ["anthologyApprovedMedia"] });
      queryClient.invalidateQueries({
        queryKey: ["content-status", currentOrgId],
      });
      queryClient.invalidateQueries({
        queryKey: ["anthology-content-type-summary"],
      });

      if (!stopInvalidation) {
        queryClient.invalidateQueries({ queryKey: ["module-all-content"] });
        queryClient.invalidateQueries({
          queryKey: ["module-all-content-count"],
        });
      }
    },
  });
};

export const useRemoveAllMediaFromCollection = () => {
  const currentOrgId = useCurrentOrgId();
  return useDelete(getCollectionMediaUrl, {
    queriesToInvalidate: [
      ["search"],
      ["collection", ["id"]],
      ["collection-approved-content", currentOrgId, ["id"]],
      ["collection-content", ["id"]],
      ["collection-content-type-summary", currentOrgId, ["id"]],
      ["moduleApprovedContent"],
      ["anthologyApprovedMedia"],
      ["content-status"],
      ["anthology-content-type-summary"],
      ["unorganized-content-groups"],
    ],
  });
};

export const useCollectionDelete = () => {
  const queryClient = useQueryClient();
  const currentOrgId = useCurrentOrgId();
  const updateCollection = useCollectionUpdate();
  const removeAllMediaFromCollection = useRemoveAllMediaFromCollection();
  const deleteCollectionParent = useDeleteCollectionParent();

  return useMutation({
    mutationFn: async ({ collection }) => {
      // Remove all children and parents of collection before "deleting"
      await removeAllMediaFromCollection.mutateAsync({
        resourceIds: { id: collection.id },
      });

      const parents = getCollectionParents(collection);
      if (notBlank(parents)) {
        await Promise.all(
          map(
            parent =>
              deleteCollectionParent.mutateAsync({
                resourceIds: {
                  collectionId: collection.id,
                  parentId: parent.id,
                },
              }),
            parents,
          ),
        );
      }

      return updateCollection.mutateAsync({
        resourceIds: { id: collection.id },
        data: {
          status: "DELETED",
          internalCode: null, // free up internal code value for use with other content groups
        },
      });
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["search"] });
      const { id } = variables;
      queryClient.invalidateQueries({ queryKey: ["collection", { id }] });
      queryClient.invalidateQueries({
        queryKey: ["collection-content", { id }],
      });
      queryClient.invalidateQueries({
        queryKey: ["collection-content-type-summary", currentOrgId, { id }],
      });
      queryClient.invalidateQueries({
        queryKey: ["collection-approved-content", currentOrgId, { id }],
      });
      queryClient.invalidateQueries({ queryKey: ["moduleApprovedContent"] });
      queryClient.invalidateQueries({ queryKey: ["anthologyApprovedMedia"] });
      queryClient.invalidateQueries({
        queryKey: ["content-status", currentOrgId],
      });
      queryClient.invalidateQueries({
        queryKey: ["anthology-content-type-summary"],
      });
      queryClient.invalidateQueries({
        queryKey: ["unorganized-content-groups"],
      });
      queryClient.invalidateQueries({ queryKey: ["module-content-groups"] });
      queryClient.invalidateQueries({ queryKey: ["anthologyCollections"] });
      queryClient.invalidateQueries({
        queryKey: ["anthology-collections-creator"],
      });
    },
  });
};

export const useCollectionCreate = () => {
  return usePost(getCreateCollectionUrl, {
    queriesToInvalidate: [
      ["search"],
      ["admin-media"],
      ["unorganized-content-groups"],
    ],
  });
};

export const useCollectionContent = id => {
  const url = getCollectionContentUrl(id);
  return useGet(["collection-content", { id }], url);
};

export const useCollectionApprovedContent = (id, params = {}) => {
  const orgId = useCurrentOrgId();
  const queryParams = "?ct.status=APPROVED&" + getQueryParams(params);
  const url = getCollectionContentUrl(id) + queryParams;
  return useGet(
    ["collection-approved-content", orgId, { id }, { url }],
    url,
    true,
    true,
  );
};

export const useUploadCollectionThumbnail = () => {
  return useUpload(getThumbnailUploadUrl);
};

export const useAddToCollection = () => {
  const currentOrgId = useCurrentOrgId();
  return usePost(getCollectionMediaUrl, {
    queriesToInvalidate: [
      ["admin-media"],
      ["anthologyApprovedMedia"],
      ["anthology-content-type-summary"],
      ["collection", ["id"]],
      ["collection-approved-content", currentOrgId, ["id"]],
      ["collection-content", currentOrgId, ["id"]],
      ["collection-content-type-summary", currentOrgId, ["id"]],
      ["content-status"],
      ["module-all-content"],
      ["module-all-content-count"],
      ["moduleApprovedContent"],
      ["search"],
    ],
  });
};

export const useDeleteCollectionParent = () => {
  return useDelete(getDeleteParentUrl, {
    queriesToInvalidate: [
      ["admin-media"],
      ["moduleMedia", ["id"]],
      ["moduleDirectChildren", ["id"]],
      ["module-content-groups"],
      ["anthologyCollections"],
      ["anthology-collections-creator"],
      ["unorganized-content-groups"],
    ],
  });
};

export const useCollectionContentTypeSummary = (id, params = {}) => {
  const orgId = useCurrentOrgId();
  const queryParams = "?" + getQueryParams(params);
  const url = getCollectionContentTypeSummaryUrl(id) + queryParams;
  return useGet(
    ["collection-content-type-summary", orgId, { id }, { url }],
    url,
  );
};

export const useUpdateCollectionItems = () => {
  const currentOrgId = useCurrentOrgId();

  return usePut(getCollectionMediaUrl, {
    queriesToInvalidate: [
      ["anthologyApprovedMedia"],
      ["collection", ["id"]],
      ["collection-approved-content", currentOrgId, ["id"]],
      ["collection-content", ["id"]],
      ["collection-content-type-summary", currentOrgId, ["id"]],
      ["moduleApprovedContent"],
      ["search"],
    ],
  });
};
